import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import * as Icons from '../../constants/icons';
import type { MarketSummary } from '../../hooks/useMarketAnalysis';

/**
 * 為替・株式分析の KPI タイル (最新値・前日比・52 週高安・RSI)。
 * 人事採用計画のサマリーカードと同じ見た目に揃える。
 */

export const formatPrice = (v: number | null | undefined, currency: string): string => {
  if (v == null) return '-';
  const digits = currency === 'JPY' && Math.abs(v) >= 1000 ? 0 : 2;
  return v.toLocaleString('ja-JP', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

type Props = {
  summary: MarketSummary | null;
  currency: string;
};

export default function MarketSummaryCards({ summary, currency }: Props) {
  const change = summary?.change ?? null;
  const changePct = summary?.change_pct ?? null;
  const up = change != null && change > 0;
  const down = change != null && change < 0;
  const changeColor = up ? '#059669' : down ? '#dc2626' : '#1e293b';
  const changeText = change == null ? '-' : `${change > 0 ? '+' : ''}${formatPrice(change, currency)}`;
  const changePctText = changePct == null ? '' : `(${changePct > 0 ? '+' : ''}${changePct.toFixed(2)}%)`;
  const rsi = summary?.rsi_14 ?? null;
  const rsiNote = rsi == null ? '' : rsi >= 70 ? '買われ過ぎ' : rsi <= 30 ? '売られ過ぎ' : '中立';
  const rsiColor = rsi == null ? '#1e293b' : rsi >= 70 ? '#dc2626' : rsi <= 30 ? '#059669' : '#1e293b';

  const cards = [
    {
      title: `終値 (${summary?.as_of ?? '-'})`,
      value: formatPrice(summary?.close, currency),
      unit: currency,
      icon: <Icons.ShowChart sx={{ fontSize: '18px' }} />,
      color: '#eff6ff', iconColor: '#3b82f6', valueColor: '#1e293b',
    },
    {
      title: '前日比',
      value: changeText,
      unit: changePctText,
      icon: up ? <Icons.TrendingUp sx={{ fontSize: '18px' }} /> : <Icons.TrendingDown sx={{ fontSize: '18px' }} />,
      color: up ? '#ecfdf5' : down ? '#fef2f2' : '#f8fafc', iconColor: changeColor, valueColor: changeColor,
    },
    {
      title: '52 週 高値 / 安値',
      value: `${formatPrice(summary?.high_52w, currency)} / ${formatPrice(summary?.low_52w, currency)}`,
      unit: currency,
      icon: <Icons.QueryStats sx={{ fontSize: '18px' }} />,
      color: '#f5f3ff', iconColor: '#8b5cf6', valueColor: '#1e293b',
    },
    {
      title: 'RSI (14 日)',
      value: rsi == null ? '-' : rsi.toFixed(1),
      unit: rsiNote,
      icon: <Icons.Analytics sx={{ fontSize: '18px' }} />,
      color: '#fff7ed', iconColor: '#f97316', valueColor: rsiColor,
    },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
      {cards.map((c, idx) => (
        <Card key={idx} sx={{ borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9' }}>
          <CardContent sx={{ px: 1.75, py: 1, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ p: 0.6, borderRadius: '8px', backgroundColor: c.color, color: c.iconColor, display: 'flex' }}>
                {c.icon}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', lineHeight: 1.2 }}>
                  {c.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '18px', fontWeight: 800, color: c.valueColor, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                    {c.value}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
                    {c.unit}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
